import type { LegalDocument } from './legal'

export type OrgType = 'club' | 'outfitter'

export type CreateOrgType = OrgType | 'crew'

export type PersonaRole = 'member' | 'manager' | 'guide' | 'outfitter' | 'guest' | 'client'

export type OrgBillingSummary = {
  subscriptionPastDueSince?: string
  subscriptionCancelAt?: string
  subscriptionCurrentPeriodEnd?: string
  defaultCardBrand?: string
  defaultCardLast4?: string
  defaultCardExpMonth?: number
  defaultCardExpYear?: number
}

export type OrgMembership = OrgBillingSummary & {
  orgId: string
  orgType: CreateOrgType
  role: PersonaRole
  orgName: string
  timezone?: string
  badgeUrl?: string
  bannerUrl?: string
  subscriptionStatus?: string
  orgState?: string
  clubFeaturesEnabled?: boolean
  clubFeaturesActive?: boolean
}

export type MemberContextUser = {
  id: string
  email: string
  firstName?: string
  lastName?: string
  displayName?: string
  avatarUrl?: string
}

import type { MembershipStatus } from './seat'

export type OrgInfoSummary = OrgBillingSummary & {
  id: string
  orgType: CreateOrgType
  name: string
  timezone: string
  subscriptionStatus?: string
  subscriptionTier?: string
  badgeUrl?: string
  bannerUrl?: string
  orgState?: string
  clubFeaturesEnabled?: boolean
  clubFeaturesActive?: boolean
}

export type MembershipWithOrg = {
  membership: {
    id: string
    orgId: string
    orgType?: CreateOrgType
    role: PersonaRole
    status: MembershipStatus
  }
  org: OrgInfoSummary
  activeMemberCount: number
}

export type MemberContext = {
  user: MemberContextUser
  orgMemberships: MembershipWithOrg[]
  selectedOrgType?: OrgType | 'hunter'
  selectedOutfitterId?: string
  pendingLegalDocuments?: LegalDocument[]
}

export type OrgContext = OrgBillingSummary & {
  userId?: string | null
  orgId: string | null
  orgType: OrgType | null
  role: PersonaRole | null
  isManager: boolean
  orgTimezone?: string
  memberships: OrgMembership[]
  subscriptionStatus?: string | null
  orgState: string | null
  clubFeaturesEnabled?: boolean | null
  clubFeaturesActive?: boolean
}

export type PersonaKey =
  | 'hunter'
  | 'club-manager'
  | 'club-member'
  | 'outfitter-owner'
  | 'guide'
  | 'outfitter-member'
  | 'crew-manager'

export function derivePersona(orgType: CreateOrgType | null, role: PersonaRole | null): PersonaKey {
  if (orgType === null || role === null) return 'hunter'
  if (orgType === 'club') {
    if (role === 'manager') return 'club-manager'
    if (role === 'member') return 'club-member'
    return 'hunter'
  }
  if (orgType === 'outfitter') {
    if (role === 'outfitter') return 'outfitter-owner'
    if (role === 'guide') return 'guide'
    if (role === 'member') return 'outfitter-member'
    return 'hunter'
  }
  if (orgType === 'crew') {
    if (role === 'manager') return 'crew-manager'
    return 'hunter'
  }
  return 'hunter'
}

export const PERSONA_LABELS: Record<PersonaKey, string> = {
  hunter: 'Hunter',
  'club-manager': 'Club Manager',
  'club-member': 'Club Member',
  'outfitter-owner': 'Outfitter Owner',
  guide: 'Guide',
  'outfitter-member': 'Outfitter Member',
  'crew-manager': 'Crew Manager',
}
